import { getAllQueryString } from '@mx/util/get-all-query-string';
import { getApp } from '@mx/util/get-app';
import { objectToQuery } from '@mx/util/object-to-query';
import { Runtime } from './enum-type';

const storagePrefix = 'hybrid_pay_deduction_part_';

/**
 * 设置 sessionStorage
 * @param key
 * @param value
 */
export function setSessionStorage(key: string, value: any = '1'): void {
  if (!key) {
    return;
  }
  try {
    sessionStorage.setItem(`${storagePrefix}${key}`, value);
  } catch (e) {
    // TODO 异常埋点
  }
}

/**
 * sessionStorage 是否存在指定键（值）
 * @param key
 * @param value
 */
export function hasSessionStorage(key: string, value: any = '1'): boolean {
  let val = '';
  try {
    val = sessionStorage.getItem(`${storagePrefix}${key}`) ?? '';
  } catch (e) {
    // TODO 异常埋点
  }
  return !!val && val === value;
}

export function sleep(ms: number) {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, ms);
  });
}

// https://juejin.im/post/6844903766982918152
export function fixedBody() {
  const scrollTop =
    document.body.scrollTop || document.documentElement.scrollTop;
  document.body.style.cssText += `position:fixed;width:100%;top:-${scrollTop}px;`;
}

// https://juejin.im/post/6844903766982918152
export function looseBody() {
  const body = document.body;
  body.style.position = '';
  const top = body.style.top;
  const intTop = -parseInt(top, 10);
  document.documentElement.scrollTop = intTop;
  document.body.scrollTop = intTop;
  body.style.top = '';
}

// AppName获取顺序: 1)UA获取; 2)utm_campaign获取; 3)使用业务方传递的nb_app; 4)均未获取到默认'wap'
export function getAppName(urlUtmCampaign: string, urlNbApp: string): string {
  const fetchParam = getApp(urlUtmCampaign);
  if (fetchParam === 'unknown-app' || fetchParam === 'web') {
    return urlNbApp || 'wap';
  }
  return fetchParam;
}

/**
 *
 * 版本号比较，0 等于，-1 小于， 1大于
 * @export versionStringCompare
 * @param {string} [preVersion='']
 * @param {string} [lastVersion='']
 * @return {*}
 */
export function versionStringCompare(preVersion = '', lastVersion = '') {
  const sources = preVersion.split('.');
  const dests = lastVersion.split('.');
  const maxL = Math.max(sources.length, dests.length);
  let result = 0;
  for (let i = 0; i < maxL; i++) {
    const preValue = sources.length > i ? sources[i] : `${0}`;
    const preNum = Number.isNaN(Number(preValue))
      ? preValue.charCodeAt(0)
      : Number(preValue);
    const lastValue = dests.length > i ? dests[i] : `${0}`;
    const lastNum = Number.isNaN(Number(lastValue))
      ? lastValue.charCodeAt(0)
      : Number(lastValue);
    if (preNum < lastNum) {
      result = -1;
      break;
    } else if (preNum > lastNum) {
      result = 1;
      break;
    }
  }
  return result;
}

// 环境映射
export const runtimeMap: string[] = ['prod', 'staging', 'test', 'dev', 'local'];

// 保证url环境与当前环境一致,拼接query参数
export function makeUpUrl(pageName: string, query = {}): string {
  const baseUrl =
    window.location.origin +
    window.location.pathname.replace(/\w+\.html/, `${pageName}.html`);
  const mark = window.location.search ? '&' : '?';
  return `${baseUrl}${window.location.search}${mark}${objectToQuery({
    ...query,
  })}`;
}

// 获取ab测试的类型，埋点使用
// 弹窗类型
// 1-老样式；2-产品通用优惠+无提额提醒；3-产品通用优惠+有限额提醒；4-下挂优惠+无提额提醒；5-下挂优惠+有限额提醒
export function getABTestType(
  windowData: ApiOneClickPayDispatcher.HalfScreenWindow | null
): number {
  const paymodelist = windowData?.adjustPayList?.userPayList[0].payModeList;
  // 下挂优惠
  let hasdesc = false;
  paymodelist?.forEach((paymode) => {
    if (paymode.promoLabel) {
      hasdesc = true;
    }
  });
  // 通用优惠
  const promoContent = !!windowData?.button?.upBtn?.promoContent;
  // 提额提醒
  const tips = !!windowData?.titleModel?.tips;
  if (hasdesc && !tips) {
    return 4;
  }
  if (promoContent && !tips) {
    return 2;
  }
  return 1;
}

/**
 * 格式化 Error 对象，用于统计埋点
 * @param err Error 对象
 */
export function formatError(err: any) {
  if (!err || !(err !== null && typeof err === 'object')) {
    return { raw_error: err };
  }
  const errObj = {
    name: err.name || '',
    message: err.message || '',
    ...err,
  };
  return errObj;
}

/**
 * 根据当前环境返回支付侧origin 地址，全部对接主干
 */
export function getOrigin(runtime: Runtime): string {
  const originMap = {
    [Runtime.Dev]: 'http://stable.pay.test.sankuai.com',
    [Runtime.Test]: 'http://stable.pay.test.sankuai.com',
    [Runtime.Staging]: 'https://stable-pay.st.meituan.com',
    [Runtime.Prod]: 'https://npay.meituan.com',
    [Runtime.Local]: 'http://stable.pay.test.sankuai.com',
  };
  return originMap[runtime];
}

/**
 * 去掉 url 上冗余参数，防止各个页面跳转，参数重复或者异常
 * @param keys
 * @returns
 */
export function omitURLQuery(keys: string[]) {
  const urlParams = getAllQueryString();
  keys.forEach((key: string) => {
    if (urlParams[key]) {
      delete urlParams[key];
    }
  });
  return urlParams;
}

export function webLogin(runtime: Runtime) {
  const loginParams = {
    risk_partner: 1,
    risk_app: 216,
    risk_platform: 3,
    backurl: window.location.href,
  };
  const mtLoginUrl = {
    [Runtime.Dev]: 'https://passport.fe.test.sankuai.com/useraccount/ilogin',
    [Runtime.Test]: 'https://passport.fe.test.sankuai.com/useraccount/ilogin',
    [Runtime.Local]: 'https://passport.fe.test.sankuai.com/useraccount/ilogin',
    [Runtime.Staging]: 'https://passport.meituan.com/useraccount/ilogin',
    [Runtime.Prod]: 'https://passport.meituan.com/useraccount/ilogin',
  };
  window.location.href = `${mtLoginUrl[runtime]}?${objectToQuery(loginParams)}`;
}